import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Loader from '../../components/common/Loader';
import ImageComponent from '../../components/common/Image';
import { logout } from '../../slices/auth/authSlice';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../components/common/Modal';
import { formatDateToDDMMYYYY } from '../../utils/functions';
import DatePicker from '../../components/common/DatePicker';
import { fetchLeadDetails } from '../../slices/eligibility/eligibilitySlice';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const PAGE_LIMIT = 10;

const STATUS_OPTIONS = [
    { value: 'hot', label: 'Hot' },
    { value: 'warm', label: 'Warm' },
    { value: 'cold', label: 'Cold' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const hasValue = (v) => v !== undefined && v !== null && v !== '';

const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    
    // Replace underscores with spaces, split into words, and format each word
    return str
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
        case 'inprogress': return 'inprogress';
        case 'approved':
        case 'disbursed': return 'completed';
        case 'cancelled': return 'Reschdule';
        case 'rejected': return 'reject';
        default: return 'inprogress';
    }
};

const formatDateToISO = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const LeadContent = ({ searchTerm, setSearchTerm, showSearchModal, setShowSearchModal }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationParams = location.state || {};

    const { isLoading } = useSelector((state) => state.dashboard);
    const { userData } = useSelector((state) => state.auth);

    const userName = userData?.response_data?.data?.userName || '';
    const userID = userData?.response_data?.data?.userID || '';
    const emailID = userData?.response_data?.data?.emailID || '';

    // ── List state ────────────────────────────────────────────────────────
    const [loader, setLoader] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false); // inline spinner for appended pages
    const [applications, setApplications] = useState([]);   // flat accumulated list
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    // ── Filter state ──────────────────────────────────────────────────────
    const [selectedStatus, setSelectedStatus] = useState('hot');
    const [selectedDealerCode, setSelectedDealerCode] = useState(navigationParams.dealer_code || 'all');
    const [selectedDealerName, setSelectedDealerName] = useState('All');
    const [tempSelectedDealerCode, setTempSelectedDealerCode] = useState(selectedDealerCode);
    const [tempSelectedDealerName, setTempSelectedDealerName] = useState(selectedDealerName);

    // Modal visibility
    const [showDealerModal, setShowDealerModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showDateModal, setShowDateModal] = useState(false);

    // Date range — default: today back to 2 months ago
    const today = new Date();
    const [dateFrom, setDateFrom] = useState(formatDateToISO(today));
    const [dateTo, setDateTo] = useState(() => {
        const d = new Date(today);
        d.setMonth(today.getMonth() - 2);
        return formatDateToISO(d);
    });

    // ── Refs ──────────────────────────────────────────────────────────────
    const searchDebounceRef = useRef(null);
    const sentinelRef = useRef(null);   // invisible div anchored after last card
    const observerRef = useRef(null);   // IntersectionObserver instance
    const isFetchingRef = useRef(false);  // true while any fetch is in-flight
    const activeFiltersRef = useRef({ ranking: 'hot' }); // filters shared between scroll pages

    // Derived
    const hasMore = applications.length < totalCount;

    // ── Core fetch ────────────────────────────────────────────────────────
    /**
     * @param {object}  filters  - API filter fields (ranking, search, fromDate, toDate…)
     * @param {number}  page     - Page number to request
     * @param {boolean} append   - false → replace list (new filter/search)
     *                            true  → append to list (infinite scroll next page)
     */
    const fetchApplicationData = useCallback(async (filters = {}, page = 1, append = false) => {
        // Prevent two concurrent fetches
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        // Persist the active filters so scroll-triggered pages reuse them
        if (!append) {
            activeFiltersRef.current = filters;
        }

        // Show full-page loader for fresh fetches; inline spinner for appended pages
        append ? setIsFetchingMore(true) : setLoader(true);

        const payload = {
            filters: {
                ...activeFiltersRef.current,
                ...filters,
                "sfe_id": userID

            },
            options: {
                page,
                limit: PAGE_LIMIT,
            },
        };

        try {
            const res = await dispatch(fetchLeadDetails(payload)).unwrap();
            if (res?.response_code === 'S100') {
                const incoming = res?.response_data?.data || [];
                const serverTotal = res?.response_data?.totalCount || 0;

                setTotalCount(serverTotal);
                setCurrentPage(page);

                if (append) {
                    // Append next page's records after the existing ones
                    setApplications((prev) => [...prev, ...incoming]);
                } else {
                    // Fresh filter / search — replace list entirely
                    setApplications(incoming);
                }
            } else {
                toast.error(res?.response_string || 'Failed to fetch applications');
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to fetch applications');
        } finally {
            append ? setIsFetchingMore(false) : setLoader(false);
            isFetchingRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // ── IntersectionObserver setup ────────────────────────────────────────
    //
    // Watches an invisible sentinel <div> placed right after the last card.
    // When the sentinel enters the viewport → fetch the next page and append.
    // Observer is recreated whenever hasMore or currentPage changes so it
    // always holds fresh closure values.
    //
    useEffect(() => {
        observerRef.current?.disconnect();

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMore && !isFetchingRef.current) {
                    fetchApplicationData({}, currentPage + 1, true /* append */);
                }
            },
            { root: null, rootMargin: '0px', threshold: 0.1 }
        );

        if (sentinelRef.current) {
            observerRef.current.observe(sentinelRef.current);
        }

        return () => observerRef.current?.disconnect();
    }, [hasMore, currentPage, fetchApplicationData]);

    // ── Initial load ──────────────────────────────────────────────────────
    useEffect(() => {
        fetchApplicationData({ ranking: selectedStatus }, 1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Search — debounced, always resets to page 1 ───────────────────────
    useEffect(() => {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            const trimmed = searchTerm.trim();
            const filters = trimmed
                ? { search: trimmed }
                : { ranking: selectedStatus };
            fetchApplicationData(filters, 1, false);
        }, 400);

        return () => clearTimeout(searchDebounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // ── Dealer / status modal ─────────────────────────────────────────────
    const handleDealerSelection = (dealerCode, dealerName) => {
        setTempSelectedDealerCode(dealerCode);
        setTempSelectedDealerName(dealerName);
    };

    const handleApplyDealer = () => {
        setSelectedDealerCode(tempSelectedDealerCode);
        setSelectedDealerName(tempSelectedDealerName);
        setShowDealerModal(false);
        fetchApplicationData({ ranking: selectedStatus }, 1, false);
    };

    // ── Date filter ───────────────────────────────────────────────────────
    const handleProceedFilter = () => {
        const from = new Date(dateFrom);
        const to = new Date(dateTo);

        if (from > to) {
            toast.warning('Invalid Date Selection');
            const todayStr = formatDateToISO(new Date());
            setDateFrom(todayStr);
            setDateTo(todayStr);
            return;
        }

        fetchApplicationData(
            {
                ranking: selectedStatus,
                fromDate: `${dateFrom} 00:00:00`,
                toDate: `${dateTo} 23:59:59`,
            },
            1,
            false,
        );

        setShowDateModal(false);
    };

    // ── Misc handlers ─────────────────────────────────────────────────────
    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
        setShowProfileModal(false);
    };

    const toPascalCase = (str) => {
        if (!str) return '';
        return str
            .replace(/(?:^\w|[A-Z]|\b\w)/g, (word) => word.toUpperCase())
            .replace(/\s+/g, ''); // Removes spaces
    };

    const getStatusClass = (ranking) => {
        if (!ranking) return 'ribbon-default';

        // Normalize string (lowercase and replace spaces/hyphens with a unified format)
        const status = ranking.toLowerCase().trim().replace(/[\s_-]+/g, '');

        switch (status) {
            case 'interested':
                return 'ribbon-interested';
            case 'callback':
                return 'ribbon-callback';
            case 'notinterested':
                return 'ribbon-not-interested';
            default:
                return 'ribbon-default';
        }
    };

    const getData = (agreementNo) => navigate(`/leaddetails?loanId=${agreementNo}`);

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <>
            <div className="container-fluid kyc-tab-dash-app dashboard-bg height-scroll">

                {/* Full-page loader — only for fresh (non-append) fetches */}
                {loader && <Loader />}

                {/* ── Filters row ──────────────────────────────────────────── */}
                <div className="row">
                    <div className="col-md-12">
                        <label htmlFor="dealer-select" className="form-label">Status</label>
                        <div className="d-flex align-items-center gap-2">
                            <div
                                className="has-icon w-100"
                                onClick={() => setShowDealerModal(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setShowDealerModal(true);
                                    }
                                }}
                                tabIndex={0}
                                role="button"
                                style={{ cursor: 'pointer' }} /* Makes the whole div look clickable */
                            >
                                <input
                                    className="form-control"
                                    placeholder="Hot"
                                    type="text"
                                    value={toPascalCase(selectedStatus)}
                                    readOnly
                                    style={{ pointerEvents: 'none' }} /* Prevents input from blocking the div's click */
                                />
                                <i className="icon-arrowdown-L1 custom-app-arrow" />
                            </div>
                            <div
                                className="filter-tab"
                                onClick={() => setShowDateModal(true)}
                                onKeyDown={(e) => e.key === 'Enter' && setShowDateModal(true)}
                                tabIndex={0}
                                role="button"
                            >
                                <i className="icon-settingh text-white fw-bold" />
                            </div>
                        </div>

                        <p className="text-secondary mt-3 text-uppercase mb-0">
                            Total Leads: <span className="fw-bold">{totalCount}</span>
                        </p>

                        {applications.length > 0 && hasMore && (
                            <p className="text-secondary small mb-0">
                                Showing {applications.length} of {totalCount} applications
                            </p>
                        )}
                    </div>
                </div>

                {/* ── Application cards ────────────────────────────────────── */}
                <div className="mb-2">
                    <div className="row gy-3">
                        {applications.length > 0 ? (
                            <>
                                {applications.map((application, index) => (
                                    <div key={application.application_id || index} className="col-md-12">
                                        <div
                                            className="db-item border-0 position-relative application-list"
                                            onClick={() => getData(application?.tc_lead_id)}
                                            onKeyDown={(e) => e.key === 'Enter' && getData(application?.tc_lead_id)}
                                            tabIndex={0}
                                            role="button"
                                        >
                                            <div className="name-detail-app align-items-center">
                                                <div>
                                                    <p className="fw-semibold mb-0 small">
                                                        {[application.first_name?.trim(), application.last_name?.trim()]
                                                            .filter(Boolean).join(' ') || '-'}
                                                    </p>
                                                    <ul>
                                                        <li className="small text-body-secondary">
                                                            {application.application_id}
                                                        </li>
                                                    </ul>
                                                </div>
                                                <div>
                                                    <span className="badge consumer-D text-white fs-6 d-none">CD</span>
                                                </div>
                                            </div>

                                            <div className={`ribbon inprogress`}>
                                                {capitalizeFirstLetter(application?.ranking)}
                                            </div>

                                            <div className={`ribbon-right ${getStatusClass(application.lead_stage)}`}>
                                                {capitalizeFirstLetter(application?.lead_stage)}
                                            </div>

                                            <div className="dash-table dash-table-app">
                                                <div className="row app-card w-100">
                                                    <div className="col-4">
                                                        <p>
                                                            <label className="fw-normal">Mobile No</label>
                                                            <span className="d-block text-body-secondary fw-semibold">
                                                                {application?.mobile_no}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <div className="col-4 text-center" />
                                                    <div className="col-4 text-end">
                                                        <p>
                                                            <label className="fw-normal">Created Date</label>
                                                            <span className="d-block text-body-secondary fw-semibold">
                                                                {formatDateToDDMMYYYY(application?.created_at)}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <ApplicationMetaRow application={application} />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/*
                                 * ── Scroll sentinel ──────────────────────────────────
                                 * Rendered only while there are more pages to fetch.
                                 * IntersectionObserver targets this element; entering
                                 * the viewport triggers the next page append.
                                 * Shows a small spinner while the next page is loading.
                                 */}
                                {hasMore && (
                                    <div ref={sentinelRef} className="col-12 text-center py-3">
                                        {isFetchingMore && (
                                            <div className="d-flex justify-content-center align-items-center gap-2 text-secondary small">
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Loading more…
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* End-of-list indicator once all records are loaded */}
                                {!hasMore && (
                                    <div className="col-12 text-center py-2">
                                        <p className="text-secondary small mb-0">
                                            All {totalCount} applications loaded
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            !loader && (
                                <div className="col-12 text-center">
                                    <p className="text-secondary">
                                        {searchTerm
                                            ? `No Leads found matching "${searchTerm}"`
                                            : 'No Leads found'}
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* ── Status / Dealer Modal ─────────────────────────────────────── */}
            <Modal
                id="SelectRole"
                show={showDealerModal}
                onClose={() => setShowDealerModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader><div className="top-draw" /></ModalHeader>

                <div className="d-flex align-items-center justify-content-between px-3 py-3 w-100">
                    <h6 className="mb-1">Lead Status</h6>
                    <button type="button" className="btn-close" onClick={() => setShowDealerModal(false)} aria-label="Close" />
                </div>

                <ModalBody>
                    <ul className="list-group list-group-flush">
                        {STATUS_OPTIONS.map((option, index) => (
                            <li
                                key={index}
                                className={`list-group-item border-0 text-secondary ${selectedStatus === option.value ? 'dealer-selected' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedStatus(option.value)}
                                onKeyDown={(e) => e.key === 'Enter' && setSelectedStatus(option.value)}
                                tabIndex={0}
                                role="button"
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </ModalBody>

                <ModalFooter>
                    <button type="button" className="btn btn-primary w-100" onClick={handleApplyDealer}>
                        Apply
                    </button>
                </ModalFooter>
            </Modal>

            {/* ── Search Modal ──────────────────────────────────────────────── */}
            <Modal
                id="searchrole"
                show={showSearchModal}
                onClose={() => setShowSearchModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader><div className="top-draw" /></ModalHeader>

                <div className="d-flex align-items-center justify-content-between custom-lr-padding">
                    <h6 className="mb-1">Search</h6>
                    <button type="button" className="btn-close" onClick={() => setShowSearchModal(false)} aria-label="Close" />
                </div>

                <ModalBody className="searchrole">
                    <div className="col-12 col-lg-auto mb-3 mb-lg-0 me-lg-3 position-relative">
                        <input
                            type="search"
                            className="form-control input-underline"
                            placeholder="Search Name / Mobile No. / Loan ID"
                            aria-label="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="has-i-search hidden-search-box applications-search">
                            <span className="icon-searchnormal fs-4 inside-search border-0" />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-12 RightPane">
                            <div className="d-flex justify-content-between align-items-center py-2 mt-3">
                                <ul className="nav nav-pills scroll-hidden d-none" id="pills-tab" role="tablist">
                                    <li className="nav-item px-2 py-2 current-primary" role="presentation">
                                        <i className="icon-settingh fw-bold" />
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" data-bs-toggle="pill" data-bs-target="#pills-appli" type="button" role="tab">Date</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link active" data-bs-toggle="pill" data-bs-target="#pills-coappli" type="button" role="tab">Dealer</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" data-bs-toggle="pill" data-bs-target="#pills-date" type="button" role="tab">Stage</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" data-bs-toggle="pill" data-bs-target="#pills-prod" type="button" role="tab">Products</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" data-bs-toggle="pill" data-bs-target="#pills-price" type="button" role="tab">Price</button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </Modal>

            {/* ── Profile Modal ─────────────────────────────────────────────── */}
            <Modal
                id="profilerole"
                show={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader><div className="top-draw" /></ModalHeader>

                <div className="d-flex align-items-center justify-content-end px-4 pt-2 w-100">
                    <button type="button" className="btn-close" onClick={() => setShowProfileModal(false)} aria-label="Close" />
                </div>

                <ModalBody>
                    <div className="row gy-4 p-1">
                        <div className="col-md-12">
                            <div className="d-flex align-items-center gap-2">
                                <ImageComponent fileName="gender-icon.svg" alt="profilepop" />
                                <div className="name fw-semibold">
                                    {userName}<br />
                                    <span className="text-secondary fw-normal">{emailID}</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-12">
                            <div className="d-flex align-items-center justify-content-between gap-4">
                                <div className="d-flex gap-4 align-items-center">
                                    <i className="icon-notification01 fs-4 dark-blue" />{' '}Notifications
                                </div>
                                <div className="noti-count text-white">2</div>
                            </div>
                        </div>
                        <div className="col-md-12">
                            <button className="text-decoration-none text-black bg-white border-0 p-0" onClick={handleLogout}>
                                <div className="d-flex gap-4 align-items-center">
                                    <i className="icon-arrowlogout-E1 fs-4 dark-blue" />{' '}Logout
                                </div>
                            </button>
                        </div>
                    </div>
                </ModalBody>
            </Modal>

            {/* ── Date Filter Modal ─────────────────────────────────────────── */}
            <Modal
                id="FilterModal"
                show={showDateModal}
                onClose={() => setShowDateModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader><div className="top-draw" /></ModalHeader>

                <div className="d-flex align-items-center justify-content-between px-4 pt-4 w-100">
                    <h6 className="mb-1 text-dark">Date Selection</h6>
                    <button type="button" className="btn-close" onClick={() => setShowDateModal(false)} aria-label="Close" />
                </div>

                <ModalBody>
                    <ul className="list-group list-group-flush">
                        <div className="row gy-3">
                            <div className="col-6">
                                <DatePicker value={dateFrom} onChange={setDateFrom} label="Date From" disableFutureDates={true} required />
                            </div>
                            <div className="col-6">
                                <DatePicker value={dateTo} onChange={setDateTo} label="Date To" disableFutureDates={true} required />
                            </div>

                            <div className="col-md-12 d-none">
                                <label className="form-label" htmlFor="status">Status</label>
                                <select
                                    id="status"
                                    className="form-select input-underline"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-sm-12 dashboard-date-top">
                                <div className="d-flex justify-content-end">
                                    <button className="btn btn-primary" type="button" onClick={handleProceedFilter}>
                                        Proceed
                                    </button>
                                </div>
                            </div>
                        </div>
                    </ul>
                </ModalBody>
            </Modal>
        </>
    );
};

// ---------------------------------------------------------------------------
// Sub-component: dynamic bottom meta row (App. Amt / Brand / Price)
// ---------------------------------------------------------------------------
const ApplicationMetaRow = ({ application }) => {
    const hasAmt = hasValue(application?.approval_amt);
    const hasBrand = hasValue(application?.brand);
    const hasPrice = hasValue(application?.price);

    const visibleCount = [hasAmt, hasBrand, hasPrice].filter(Boolean).length;
    const colClass = visibleCount === 3 ? 'col-4' : visibleCount === 2 ? 'col-6' : 'col-12';

    if (!visibleCount) return null;

    return (
        <div className="row app-card pb-0 w-100">
            {hasAmt && (
                <div className={colClass}>
                    <p>
                        <label className="fw-normal">App. Amt</label>
                        <span className="d-block text-body-secondary fw-semibold">{application.approval_amt}</span>
                    </p>
                </div>
            )}
            {hasBrand && (
                <div className={`${colClass} text-center`}>
                    <p>
                        <label className="fw-normal">Brand</label>
                        <span className="d-block text-body-secondary fw-semibold">
                            {capitalizeFirstLetter(application.brand)}
                        </span>
                    </p>
                </div>
            )}
            {hasPrice && (
                <div className={`${colClass} text-end`}>
                    <p>
                        <label className="fw-normal">Price</label>
                        <span className="d-block text-body-secondary fw-semibold">{application.price}</span>
                    </p>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// PropTypes
// ---------------------------------------------------------------------------
LeadContent.propTypes = {
    searchTerm: PropTypes.string.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    showSearchModal: PropTypes.bool.isRequired,
    setShowSearchModal: PropTypes.func.isRequired,
};

export default LeadContent;
